import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useUserRole } from '@/hooks/useUserRole'
import { IMAGE_SLOTS, type SlotKey } from '../slotRegistry'
import { useImageSlot } from '../hooks/useImageSlot'
import { DesignerOverlay } from './DesignerOverlay'
import { DesignerSlidePanel } from './DesignerSlidePanel'
import { SlotOverlay } from './SlotOverlay'

interface SlotImageProps {
  slotKey: SlotKey
  className?: string
  fallback?: ReactNode
  alt?: string
  /** Fade the image in after it finishes loading (default: false, renders immediately). */
  fadeIn?: boolean
}

export function SlotImage({ slotKey, className, fallback, alt, fadeIn = false }: SlotImageProps) {
  const { assignment, loading } = useImageSlot(slotKey)
  const { canDesign } = useUserRole()
  const [panelOpen, setPanelOpen] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)

  const objectPosition = assignment
    ? `${assignment.focusX * 100}% ${assignment.focusY * 100}%`
    : 'center'

  return (
    <div className={cn('relative group/slot overflow-hidden', className)}>
      {assignment?.mediaUrl ? (
        <img
          src={assignment.mediaUrl}
          alt={alt ?? IMAGE_SLOTS[slotKey].label}
          className={cn(
            'h-full w-full object-cover',
            fadeIn && 'transition-opacity duration-700 ease-out',
            fadeIn && !imgLoaded && 'opacity-0',
          )}
          style={{ objectPosition }}
          onLoad={() => setImgLoaded(true)}
        />
      ) : !loading ? (
        fallback
      ) : null}

      {assignment?.overlay && <SlotOverlay overlay={assignment.overlay} />}

      {canDesign && (
        <>
          <DesignerOverlay
            slotKey={slotKey}
            assignment={assignment}
            onEdit={() => setPanelOpen(true)}
          />
          <DesignerSlidePanel
            slotKey={slotKey}
            currentAssignment={assignment}
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
          />
        </>
      )}
    </div>
  )
}
